import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  TextField,
  Box,
  Alert,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  InputAdornment,
  Paper,
} from "@mui/material";
import { keyframes, styled } from "@mui/system";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Settings, Wrench, Factory, Cpu, Hammer, Cog } from "lucide-react";
import { getDbHeader, getErrorResponse } from "../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../utils/constants";
import { saveToken } from "../utils/utils2";
import { UserContext } from "../context/UserProvider";
import MuPb from "../widgets/MuPb";

// 1. Keyframes
const float = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(15deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

const floatReverse = keyframes`
  0% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(30px) rotate(-15deg); }
  100% { transform: translateY(0px) rotate(0deg); }
`;

// 2. Use a styled() component instead of sx-string interpolation.
// This attaches the animation via emotion's `css` at style-creation time,
// which sidesteps any sx-parsing edge cases and lets us force the animation
// with !important so a global prefers-reduced-motion reset can't silently
// zero out the duration.
const FloatingIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "anim" && prop !== "delay",
})(({ anim, delay }) => ({
  position: "absolute",
  opacity: 0.12, // bumped up temporarily so you can actually see it's working; tune down once confirmed
  color: "inherit",
  zIndex: 0,
  pointerEvents: "none",
  animation: `${anim} 10s ease-in-out infinite`,
  animationDelay: delay,
  "@media (prefers-reduced-motion: reduce)": {
    animation: `${anim} 10s ease-in-out infinite !important`,
  },
}));

const FloatingIcon = ({
  Icon,
  top,
  left,
  right,
  bottom,
  size,
  animation,
  delay,
}) => (
  <FloatingIconBox
    anim={animation}
    delay={delay}
    sx={{ top, left, right, bottom, color: "primary.main" }}
  >
    <Icon size={size} strokeWidth={1} />
  </FloatingIconBox>
);

function Login() {
  const navigate = useNavigate();
  const { user, loading: loadingUser, reloadUser } = useContext(UserContext);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!loadingUser && user) {
      navigate("/", { replace: true });
    }
  }, [user, loadingUser, navigate]);

  if (loadingUser) {
    return <MuPb />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (username === "") {
      toast.error("Username is required");
    } else if (password === "") {
      toast.error("Password is required");
    } else {
      try {
        setLoading(true);
        const { data } = await axios.post(
          `${API_ENDPOINT}/user/login`,
          { username, password },
          getDbHeader()
        );
        saveToken(data.data.token);
        await reloadUser();
        navigate(`/`);
      } catch (error) {
        setError(getErrorResponse(error));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    // 3. Give this box an explicit zIndex so it formally establishes its
    // own stacking context. Without this, position:relative + z-index:auto
    // does NOT create a stacking context, and z-index on descendants can
    // behave unpredictably across browsers.
    <Box
      sx={{
        position: "relative",
        zIndex: 0,
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: (theme) => theme.palette.background.default,
        overflow: "hidden",
      }}
    >
      <FloatingIcon
        Icon={Settings}
        top="10%"
        left="5%"
        size={120}
        animation={float}
        delay="0s"
      />
      <FloatingIcon
        Icon={Factory}
        top="60%"
        left="15%"
        size={150}
        animation={floatReverse}
        delay="2s"
      />
      <FloatingIcon
        Icon={Wrench}
        top="20%"
        right="10%"
        size={100}
        animation={floatReverse}
        delay="1s"
      />
      <FloatingIcon
        Icon={Cpu}
        top="70%"
        right="5%"
        size={140}
        animation={float}
        delay="3s"
      />
      <FloatingIcon
        Icon={Hammer}
        top="40%"
        left="80%"
        size={90}
        animation={float}
        delay="1.5s"
      />
      <FloatingIcon
        Icon={Cog}
        top="-5%"
        left="40%"
        size={180}
        animation={floatReverse}
        delay="0.5s"
      />

      <Container maxWidth="xs" sx={{ zIndex: 1, position: "relative" }}>
        <Paper
          elevation={4}
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 3,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 8px 32px rgba(2, 71, 95, 0.1)",
          }}
        >
          <Box component="div" sx={{ width: 180, marginBottom: 3 }}>
            <img
              src="https://s3.ap-south-1.amazonaws.com/expoplanner.in/download.png"
              alt="Company Logo"
              width="100%"
              style={{ display: "block", margin: "0 auto" }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            autoComplete="off"
            sx={{ width: "100%" }}
          >
            <TextField
              label="Username"
              variant="outlined"
              fullWidth
              required
              size="medium"
              margin="normal"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": { backgroundColor: "#ffffff" },
              }}
            />

            <FormControl
              variant="outlined"
              size="medium"
              required
              fullWidth
              sx={{ mt: 2 }}
            >
              <InputLabel htmlFor="standard-adornment-password">
                Password
              </InputLabel>
              <OutlinedInput
                id="standard-adornment-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                sx={{ backgroundColor: "#ffffff" }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                    >
                      {!showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label="Password"
              />
            </FormControl>

            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ mt: 4, mb: 1, py: 1.2, borderRadius: 2 }}
              loading={loading}
            >
              Login
            </LoadingButton>
          </Box>
        </Paper>
      </Container>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          p: 2,
          textAlign: "center",
          zIndex: 1,
        }}
      >
        <Typography variant="body2" color="textSecondary">
          © {new Date().getFullYear()} Expoplanner. All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
}

export default Login;
